import React from 'react';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  bordered?: boolean;
  hoverable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  responsive?: boolean;
  dense?: boolean;
}

export interface TableHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  active?: boolean;
}

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  width?: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className = '', striped = false, bordered = false, hoverable = false, size = 'md', responsive = false, dense = false, children, ...props }, ref) => {
    const sizeClasses = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    const tableClasses = `
      w-full
      bg-white dark:bg-gray-800 
      text-gray-900 dark:text-gray-100
      ${sizeClasses[size]}
      ${striped ? 'table-striped' : ''}
      ${bordered ? 'border-collapse border border-gray-200 dark:border-gray-700' : ''}
      ${hoverable ? 'hover:table-hover' : ''}
      ${dense ? 'table-dense' : ''}
      ${className}
    `;

    const table = (
      <table ref={ref} className={tableClasses} {...props}>
        {children}
      </table>
    );

    if (responsive) {
      return (
        <div className="overflow-x-auto">
          {table}
        </div>
      );
    }

    return table;
  }
);

Table.displayName = 'Table';

export const TableHead = React.forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <thead
        ref={ref}
        className={`bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 ${className}`}
        {...props}
      >
        {children}
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

export const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <tbody
        ref={ref}
        className={`divide-y divide-gray-200 dark:divide-gray-700 ${className}`}
        {...props}
      >
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className = '', active = false, children, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={`
          ${active ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}
          ${className}
        `}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className = '', align = 'left', width, children, ...props }, ref) => {
    const alignClasses = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    };

    return (
      <td
        ref={ref}
        className={`
          px-4 py-3
          whitespace-nowrap
          ${alignClasses[align]}
          ${className}
        `}
        style={width ? { width } : undefined}
        {...props}
      >
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

export const TableHeaderCell = React.forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  ({ className = '', align = 'left', width, sortable = false, sortDirection = null, onSort, children, ...props }, ref) => {
    const alignClasses = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    };

    const renderSortIcon = () => {
      if (!sortable) return null;
      
      if (sortDirection === 'asc') {
        return <span className="ml-1">↑</span>;
      }
      
      if (sortDirection === 'desc') {
        return <span className="ml-1">↓</span>;
      }
      
      return <span className="ml-1 text-gray-400">↕</span>;
    };

    return (
      <th
        ref={ref}
        className={`
          px-4 py-3
          font-semibold
          ${alignClasses[align]}
          ${sortable ? 'cursor-pointer select-none' : ''}
          ${className}
        `}
        style={width ? { width } : undefined}
        onClick={sortable ? onSort : undefined}
        {...props}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          {renderSortIcon()}
        </div>
      </th>
    );
  }
);

TableHeaderCell.displayName = 'TableHeaderCell';

// For convenience, expose the components as a single Table object
const TableNamespace = Object.assign(Table, {
  Head: TableHead,
  Body: TableBody,
  Row: TableRow,
  Cell: TableCell,
  HeaderCell: TableHeaderCell,
});

export default TableNamespace;